import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Card } from '@parrot-software/design-system';
import { CircleDollarSign, CircleOff } from '@parrot-software/design-system/icons';
import { logger } from '@parrot-software/observability';

import { REACTIVATION_ORIGIN } from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { formatPaymentDate } from '@subscriptions/presentation/components/specific/SubscriptionsList/utils';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });

interface BlockChurnCardProps {
  blockUuid: string;
  churnDate?: string | null;
}

/**
 * Right-column card of a churned subscription: it replaces the payment method card, since a churned
 * block has no active method to manage.
 *
 * The copy drops its first sentence when the churn date is missing, which is permanent for blocks
 * churned before the add-ons release and, until the backend starts sending it, the case for every
 * block. The reactivation button enters the wizard at the payment step with the origin that turns
 * the Stripe session into a reactivation charge.
 *
 * @param {BlockChurnCardProps} props - Component properties
 * @returns {React.ReactElement} Churn card
 */
export const BlockChurnCard = ({ blockUuid, churnDate }: BlockChurnCardProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const areActionsLocked = useAreSubscriptionActionsLocked();

  const churnDateLabel = churnDate ? formatPaymentDate(churnDate) : '';
  const hasChurnDate = Boolean(churnDateLabel) && churnDateLabel !== '-';

  const handleReactivate = () => {
    subLog.info('openReactivationWizard', { 'block.uuid': blockUuid, hasChurnDate });
    history.push(`/subscriptions/blocks/create/${blockUuid}/payment?origin=${REACTIVATION_ORIGIN}`);
  };

  return (
    <Card className="flex-1 p-6 font-sans shadow-xs">
      <div className="flex h-full flex-col gap-3">
        <p className="text-sm text-muted-foreground">{t('blockDetail.churn.title')}</p>

        <p className="flex items-center gap-2 text-base font-semibold text-destructive">
          <CircleOff aria-hidden className="h-4 w-4 shrink-0" />
          {t('blockDetail.churn.status')}
        </p>

        <p className="text-sm leading-5 text-foreground">
          {hasChurnDate
            ? t('blockDetail.churn.descriptionWithDate', { date: churnDateLabel })
            : t('blockDetail.churn.description')}
        </p>

        <div className="mt-auto flex flex-col gap-2 pt-2">
          <Button className="min-h-11 w-full" disabled={areActionsLocked} onClick={handleReactivate}>
            <CircleDollarSign aria-hidden className="mr-2 h-4 w-4" />
            {t('blockDetail.churn.action')}
          </Button>

          <p className="text-xs leading-4 text-muted-foreground">{t('blockDetail.churn.note')}</p>
        </div>
      </div>
    </Card>
  );
};
